// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN INVESTMENTS PAGE                        ║
// ║  With expandable rows for simulation controls and live charts    ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  RefreshCw,
  DollarSign,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Activity,
  ChevronDown,
  ChevronUp,
  Globe,
  Cpu
} from 'lucide-react'
import { InvestmentsAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'
import MarketSimulatorPanel from '../components/MarketSimulatorPanel'
import TradingChart from '../../components/TradingChart'

export default function InvestmentsPage() {
  const { hasPermission } = useAdmin()
  const [securities, setSecurities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)
  const [expandedRow, setExpandedRow] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingSecurity, setEditingSecurity] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSecuritiesQuiet()
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [securitiesRes, statsRes] = await Promise.all([
        InvestmentsAdminAPI.getSecurities(),
        InvestmentsAdminAPI.getStats()
      ])
      if (securitiesRes.success) {
        setSecurities(securitiesRes.data.securities || securitiesRes.data || [])
      } else {
        setError(securitiesRes.message || 'Failed to load securities')
      }
      if (statsRes.success) {
        setStats(statsRes.data)
      }
    } catch (err) {
      setError(`Failed to load investments data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const fetchSecuritiesQuiet = async () => {
    try {
      const securitiesRes = await InvestmentsAdminAPI.getSecurities()
      if (securitiesRes.success) {
        setSecurities(securitiesRes.data.securities || securitiesRes.data || [])
      }
    } catch (err) {
      // Silent fail
    }
  }

  const handleSaveSecurity = async (securityData) => {
    setProcessing(true)
    try {
      const res = await InvestmentsAdminAPI.saveSecurity(securityData)
      if (res.success) {
        fetchData()
        setShowModal(false)
        setEditingSecurity(null)
      }
    } catch (err) {
      console.error('Failed to save security')
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteSecurity = async (securityId) => {
    if (!confirm('Are you sure you want to delete this security?')) return
    try {
      const res = await InvestmentsAdminAPI.deleteSecurity(securityId)
      if (res.success) {
        fetchData()
      }
    } catch (err) {
      console.error('Failed to delete security')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0)
  }

  const filteredSecurities = securities.filter(sec => {
    const matchesSearch = !search ||
      sec.symbol?.toLowerCase().includes(search.toLowerCase()) ||
      sec.name?.toLowerCase().includes(search.toLowerCase())
    const matchesType = !typeFilter || sec.type === typeFilter
    return matchesSearch && matchesType
  })

  const getChangeColor = (change) => {
    if (change > 0) return 'text-emerald-400'
    if (change < 0) return 'text-red-400'
    return 'text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Investments</h1>
          <p className="text-slate-400 mt-1">Manage securities with individual simulation settings</p>
        </div>
        {hasPermission('investments.edit') && (
          <button
            onClick={() => {
              setEditingSecurity(null)
              setShowModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Security
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Securities</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.totalSecurities)}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Invested</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalInvested)}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Investors</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.activeInvestors)}</p>
              </div>
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg. Return</p>
                <p className={`text-2xl font-bold ${getChangeColor(parseFloat(stats.avgReturn) || 0)}`}>
                  {parseFloat(stats.avgReturn) > 0 ? '+' : ''}{(parseFloat(stats.avgReturn) || 0).toFixed(2)}%
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Market Simulator Panel (simplified) */}
      {hasPermission('investments.manage') && (
        <MarketSimulatorPanel onPriceUpdate={fetchData} />
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by symbol or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Types</option>
            <option value="stock">Stocks</option>
            <option value="etf">ETFs</option>
            <option value="bond">Bonds</option>
            <option value="mutual_fund">Mutual Funds</option>
          </select>
          <button
            onClick={fetchData}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Securities List */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-slate-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 py-3 px-4 bg-slate-900/50 text-sm font-medium text-slate-400">
              <div className="col-span-4">Security</div>
              <div className="col-span-1 text-center">Type</div>
              <div className="col-span-2 text-right">Price</div>
              <div className="col-span-2 text-right">Change</div>
              <div className="col-span-1 text-center">Mode</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>

            {/* Rows */}
            {filteredSecurities.map((security) => (
              <SecurityRow
                key={security.id}
                security={security}
                isExpanded={expandedRow === security.id}
                onToggleExpand={() => setExpandedRow(expandedRow === security.id ? null : security.id)}
                onEdit={() => {
                  setEditingSecurity(security)
                  setShowModal(true)
                }}
                onDelete={() => handleDeleteSecurity(security.id)}
                onUpdate={fetchSecuritiesQuiet}
                hasPermission={hasPermission}
                formatCurrency={formatCurrency}
                getChangeColor={getChangeColor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Security Modal */}
      <AnimatePresence>
        {showModal && (
          <SecurityFormModal
            security={editingSecurity}
            onClose={() => {
              setShowModal(false)
              setEditingSecurity(null)
            }}
            onSave={handleSaveSecurity}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Expandable Security Row Component
function SecurityRow({ security, isExpanded, onToggleExpand, onEdit, onDelete, onUpdate, hasPermission, formatCurrency, getChangeColor }) {
  const isRealSecurity = security.is_real_security
  const dataMode = security.data_mode || 'simulation'
  const isSimulation = dataMode === 'simulation'

  return (
    <div className="divide-y divide-slate-700/50">
      {/* Main Row */}
      <div
        className={`grid grid-cols-12 gap-4 py-3 px-4 items-center hover:bg-slate-700/30 transition-colors cursor-pointer ${
          isExpanded ? 'bg-slate-700/20' : ''
        }`}
        onClick={isSimulation ? onToggleExpand : undefined}
      >
        <div className="col-span-4 flex items-center gap-3">
          {isSimulation && (
            <div className="text-slate-500">
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          )}
          {!isSimulation && <div className="w-4" />}
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">{security.symbol?.charAt(0)}</span>
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{security.symbol}</p>
            <p className="text-slate-500 text-sm truncate">{security.name}</p>
          </div>
        </div>

        <div className="col-span-1 text-center">
          <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs font-medium capitalize">
            {security.type?.replace('_', ' ')}
          </span>
        </div>

        <div className="col-span-2 text-right">
          <span className="text-white font-semibold">{formatCurrency(security.current_price)}</span>
        </div>

        <div className="col-span-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {parseFloat(security.change_percent) > 0 ? (
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            ) : parseFloat(security.change_percent) < 0 ? (
              <TrendingDown className="w-4 h-4 text-red-400" />
            ) : null}
            <span className={`font-semibold ${getChangeColor(parseFloat(security.change_percent) || 0)}`}>
              {parseFloat(security.change_percent) > 0 ? '+' : ''}{(parseFloat(security.change_percent) || 0).toFixed(2)}%
            </span>
          </div>
        </div>

        <div className="col-span-1 text-center">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
            isSimulation
              ? 'bg-violet-500/20 text-violet-400'
              : 'bg-emerald-500/20 text-emerald-400'
          }`}>
            {isSimulation ? <Cpu className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
            {isSimulation ? 'Sim' : 'Real'}
          </span>
        </div>

        <div className="col-span-2 flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
          {hasPermission('investments.edit') && (
            <>
              <button
                onClick={onEdit}
                className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4 text-slate-400" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded Content - Only for simulation mode */}
      <AnimatePresence>
        {isExpanded && isSimulation && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <ExpandedSecurityPanel
              security={security}
              onUpdate={onUpdate}
              formatCurrency={formatCurrency}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Expanded Panel with Chart and Simulation Controls
function ExpandedSecurityPanel({ security, onUpdate, formatCurrency }) {
  const [actionLoading, setActionLoading] = useState(false)

  // Simulation settings
  const [trend, setTrend] = useState(security.simulation_trend || 'neutral')
  const [volatility, setVolatility] = useState(security.simulation_volatility || 0.02)

  // Transform security for TradingChart compatibility
  const chartSecurity = {
    id: security.id,
    symbol: security.symbol,
    name: security.name,
    current_price: security.current_price,
    change_percent: security.change_percent,
    change_amount: security.change_amount,
    type: security.type,
    security_type: security.type
  }

  // Save simulation settings
  const handleSaveSettings = async () => {
    setActionLoading(true)
    try {
      const res = await InvestmentsAdminAPI.updateSimulationSettings(security.id, {
        trend,
        volatility
      })
      if (res.success) {
        onUpdate?.()
      }
    } catch (err) {
      console.error('Failed to save settings:', err)
    } finally {
      setActionLoading(false)
    }
  }

  // Toggle data mode
  const handleToggleMode = async (newMode) => {
    if (!security.is_real_security && newMode === 'real') {
      alert('Cannot switch to real mode - this is a fictional security')
      return
    }
    setActionLoading(true)
    try {
      const res = await InvestmentsAdminAPI.toggleDataMode(security.id, newMode)
      if (res.success) {
        onUpdate?.()
      }
    } catch (err) {
      console.error('Failed to toggle mode:', err)
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="p-4 bg-slate-900/50">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart - Using the same TradingChart component as user side */}
        <div className="lg:col-span-2">
          <TradingChart security={chartSecurity} />
        </div>

        {/* Simulation Controls */}
        <div className="space-y-4">
          {/* Data Mode Toggle */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-white mb-2">Data Mode</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleToggleMode('simulation')}
                disabled={actionLoading}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  security.data_mode === 'simulation'
                    ? 'bg-violet-500 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                } disabled:opacity-50`}
              >
                <Cpu className="w-4 h-4 inline mr-1" />
                Simulation
              </button>
              <button
                onClick={() => handleToggleMode('real')}
                disabled={actionLoading || !security.is_real_security}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  security.data_mode === 'real'
                    ? 'bg-emerald-500 text-white'
                    : security.is_real_security
                      ? 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                } disabled:opacity-50`}
              >
                <Globe className="w-4 h-4 inline mr-1" />
                Real
              </button>
            </div>
            {!security.is_real_security && (
              <p className="text-xs text-slate-500 mt-2">
                Real mode unavailable - fictional security
              </p>
            )}
          </div>

          {/* Simulation Settings */}
          {security.data_mode === 'simulation' && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 space-y-3">
              <h4 className="text-sm font-semibold text-white">Simulation Settings</h4>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Trend</label>
                <select
                  value={trend}
                  onChange={(e) => setTrend(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500"
                >
                  <option value="neutral">Neutral</option>
                  <option value="bullish">Bullish (Up)</option>
                  <option value="bearish">Bearish (Down)</option>
                  <option value="volatile">Volatile</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  Volatility ({(volatility * 100).toFixed(1)}%)
                </label>
                <input
                  type="range"
                  min="0.005"
                  max="0.10"
                  step="0.005"
                  value={volatility}
                  onChange={(e) => setVolatility(parseFloat(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <button
                onClick={handleSaveSettings}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Settings
              </button>
            </div>
          )}

          {/* Security Info */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-white mb-2">Info</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">Day High</span>
                <span className="text-white">{formatCurrency(security.day_high)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Day Low</span>
                <span className="text-white">{formatCurrency(security.day_low)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Prev Close</span>
                <span className="text-white">{formatCurrency(security.previous_close)}</span>
              </div>
              {security.yahoo_symbol && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Yahoo Symbol</span>
                  <span className="text-emerald-400">{security.yahoo_symbol}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Security Form Modal Component with Yahoo Finance Validation
function SecurityFormModal({ security, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    symbol: security?.symbol || '',
    name: security?.name || '',
    type: security?.type || 'stock',
    current_price: security?.current_price || 0,
    market_cap: security?.market_cap || '',
    description: security?.description || '',
    sector: security?.sector || '',
    exchange: security?.exchange || '',
    riskLevel: security?.risk_level || 'medium',
    is_active: security?.is_active ?? true,
    isRealSecurity: security?.is_real_security ?? false,
    yahooSymbol: security?.yahoo_symbol || '',
    dataMode: security?.data_mode || 'simulation',
    simulationTrend: security?.simulation_trend || 'neutral',
    simulationVolatility: security?.simulation_volatility || 0.02,
    simulationStartDate: security?.simulation_start_date?.split('T')[0] || '',
    simulationStartPrice: security?.simulation_start_price || ''
  })

  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState(null)

  const handleValidate = async () => {
    if (!formData.name || formData.name.trim().length < 2) return

    setValidating(true)
    setValidationResult(null)

    try {
      const res = await InvestmentsAdminAPI.validateSecurityName(formData.name, formData.symbol)
      if (res.success) {
        setValidationResult(res.data)
        if (res.data.exists) {
          setFormData(prev => ({
            ...prev,
            isRealSecurity: true,
            yahooSymbol: res.data.symbol,
            symbol: res.data.symbol || prev.symbol,
            current_price: res.data.currentPrice || prev.current_price,
            market_cap: res.data.marketCap || prev.market_cap,
            exchange: res.data.exchange || prev.exchange,
            dataMode: 'real'
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            isRealSecurity: false,
            yahooSymbol: '',
            dataMode: 'simulation'
          }))
        }
      }
    } catch (err) {
      setValidationResult({ error: true, message: 'Validation failed' })
    } finally {
      setValidating(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({ ...formData, id: security?.id })
  }

  const canToggleRealMode = formData.isRealSecurity

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {security ? 'Edit Security' : 'Add Security'}
            </h2>
            <button type="button" onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {/* Name with Validate Button */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Security Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value })
                    setValidationResult(null)
                  }}
                  className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Apple Inc., Tesla, Google..."
                  required
                />
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={validating || formData.name.trim().length < 2}
                  className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    validating ? 'bg-slate-700 text-slate-400' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Validate
                </button>
              </div>

              {validationResult && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-3 p-3 rounded-lg border ${
                    validationResult.exists
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : validationResult.error
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {validationResult.exists ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${validationResult.exists ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {validationResult.message}
                      </p>
                      {validationResult.exists && (
                        <p className="text-xs text-slate-400 mt-1">
                          Current Price: ${validationResult.currentPrice?.toFixed(2)} | Exchange: {validationResult.exchange}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Symbol and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Symbol</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="AAPL"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="stock">Stock</option>
                  <option value="etf">ETF</option>
                  <option value="bond">Bond</option>
                  <option value="mutual_fund">Mutual Fund</option>
                </select>
              </div>
            </div>

            {/* Price and Risk */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Current Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: parseFloat(e.target.value) })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Risk Level</label>
                <select
                  value={formData.riskLevel}
                  onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="very high">Very High</option>
                </select>
              </div>
            </div>

            {/* Data Mode Section */}
            <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Data Mode
              </h3>

              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, dataMode: 'simulation' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    formData.dataMode === 'simulation' ? 'bg-violet-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  Simulation
                </button>
                <button
                  type="button"
                  onClick={() => canToggleRealMode && setFormData({ ...formData, dataMode: 'real' })}
                  disabled={!canToggleRealMode}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    formData.dataMode === 'real'
                      ? 'bg-emerald-600 text-white'
                      : canToggleRealMode
                        ? 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                  }`}
                >
                  Real Market
                </button>
              </div>

              {!canToggleRealMode && (
                <p className="text-xs text-slate-500 mb-3">
                  Real mode only available for validated securities. Use the "Validate" button.
                </p>
              )}

              {formData.dataMode === 'simulation' && (
                <div className="space-y-3 pt-3 border-t border-slate-700">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Trend</label>
                      <select
                        value={formData.simulationTrend}
                        onChange={(e) => setFormData({ ...formData, simulationTrend: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                      >
                        <option value="neutral">Neutral</option>
                        <option value="bullish">Bullish (Up)</option>
                        <option value="bearish">Bearish (Down)</option>
                        <option value="volatile">Volatile</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">
                        Volatility ({(formData.simulationVolatility * 100).toFixed(1)}%)
                      </label>
                      <input
                        type="range"
                        min="0.005"
                        max="0.10"
                        step="0.005"
                        value={formData.simulationVolatility}
                        onChange={(e) => setFormData({ ...formData, simulationVolatility: parseFloat(e.target.value) })}
                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer mt-2"
                      />
                    </div>
                  </div>

                  {!formData.isRealSecurity && (
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">IPO Date</label>
                        <input
                          type="date"
                          value={formData.simulationStartDate}
                          onChange={(e) => setFormData({ ...formData, simulationStartDate: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">IPO Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.simulationStartPrice}
                          onChange={(e) => setFormData({ ...formData, simulationStartPrice: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                          placeholder="Starting price"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active (visible to customers)</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Security
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
